import { FileError } from '../errors';
import { retrieveResume } from '../api/builder';

const DEFAULT_INTERVAL_MS = 2000;
// EN-1003: must outlast ResumeUploadParseJob, which can make two OpenAI attempts of
// up to 120s each (config/openai.php request_timeout, $tries = 2) plus a 10s backoff,
// the file download and queue wait. At 120s, slow parses were reported as failed
// uploads that then appeared in the resumes list anyway.
const DEFAULT_TIMEOUT_MS = 300000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * EN-950: /api/resumes/upload now parses the resume on the queue and returns 202 with
 * just {id, uuid}. This polls the existing GET /api/resumes/{resume} endpoint (which
 * already exposes `meta.parse_status`, see OnboardingResumeParseJob/ResumeUploadParseJob)
 * until the parse reaches a terminal state, so callers can keep treating upload as
 * "resolves with the finished resume, or throws".
 *
 * Takes the resume's `uuid`, not its numeric `id` — Resume::getRouteKeyName() binds
 * GET /api/resumes/{resume} by uuid, so an id here 404s.
 */
const pollResumeStatus = async (resumeUuid, { intervalMs = DEFAULT_INTERVAL_MS, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
	const deadline = Date.now() + timeoutMs;

	while (Date.now() < deadline) {
		const resume = await retrieveResume(resumeUuid);
		const status = resume?.meta?.parse_status;

		if (status === 'parsed') {
			return resume;
		}

		if (status === 'failed') {
			throw new FileError(
				resume?.meta?.parse_error || 'There was an error uploading your resume, please try with another file.',
				400
			);
		}

		await delay(intervalMs);
	}

	throw new FileError('Your resume is taking longer than expected to process. Please check back shortly.', 408);
};

export {
	pollResumeStatus
};
